import { PLAN_BLOCKER_CATEGORIES } from './constants';
import { evaluateProductEligibility } from './plan-eligibility';

/**
 * @typedef {import('./plan-eligibility').EligibilityProduct} EligibilityProduct
 * @typedef {import('./ProductUtils').Price} Price
 */

/**
 * Finds the plan to recommend for the current audience: the product owning
 * the cheapest price that fits the member count for the selected billing
 * period. Used by the focused audience modal copy and the recommended badge.
 * Computed across all products, so it must run above the per-card level.
 *
 * `isCurrentPlan` is true when the recommended price is the one the user is
 * subscribed to (the card already shows "Current plan") — consumers suppress
 * the badge and switch the modal copy in that case. When the same product
 * needs a bigger price step, it stays false: moving up is a real action.
 *
 * @param {object} options
 * @param {EligibilityProduct[]} options.availableProducts - active products that may be recommended
 * @param {EligibilityProduct[]} [options.productCatalog=options.availableProducts] - full catalogue, including the inactive current product
 * @param {number} options.currentMembers - current audience size
 * @param {object|null} options.currentSiteLimits - current site usage and active features
 * @param {'month'|'year'} options.showPeriod - selected billing period
 * @param {{base_product?: string, stripe_price_id?: string}|null} options.currentPrice - the user's subscribed price
 * @param {boolean} [options.isSubdirectory=false]
 * @param {{major: number}|null} [options.currentGhostVersion=null]
 * @param {boolean} [options.hasCustomSendingDomain=false]
 * @returns {{product: EligibilityProduct|null, price: Price|null, isCurrentPlan: boolean, hasAudienceBlocker: boolean}}
 */
export const findRecommendedPlan = ({
    availableProducts,
    productCatalog = availableProducts,
    currentMembers,
    currentSiteLimits,
    showPeriod,
    currentPrice,
    isSubdirectory = false,
    currentGhostVersion = null,
    hasCustomSendingDomain = false,
}) => {
    const products = availableProducts || [];
    const catalog = productCatalog || products;

    const eligibilityByProduct = products.map((product) => {
        if (!product?.prices?.length) {
            return null;
        }

        const eligibility = evaluateProductEligibility({
            product,
            availableProducts: products,
            currentMembers,
            currentSiteLimits,
            currentPrice,
            showPeriod,
            isSubdirectory,
            currentGhostVersion,
            hasCustomSendingDomain,
        });

        return { product, eligibility };
    });

    // The UI only presents a recommendation when at least one plan is too
    // small for the audience. Keep that signal beside the eligibility scan so
    // callers do not duplicate the member-fit calculation.
    const hasAudienceBlocker = eligibilityByProduct.some(
        (entry) => entry && !entry.eligibility.suitablePrice,
    );

    const fittingPriceFor = (entry) => {
        if (!entry) {
            return null;
        }

        const { eligibility } = entry;
        const hasEligibilityBlocker = eligibility.blockers.some(
            (blocker) => blocker.category !== PLAN_BLOCKER_CATEGORIES.CURRENT_PLAN,
        );

        return hasEligibilityBlocker ? null : eligibility.suitablePrice;
    };

    // Never steer below the plan the user is on. The recommendation answers
    // "my audience outgrew a plan" and that answer only points up: audience
    // alone must not suggest a downgrade (other usage may block the cheaper
    // plan, and shedding members is not an action we steer towards).
    const currentPriceId = currentPrice?.stripe_price_id;
    const currentBaseProduct = currentPrice?.base_product;
    const normalizedCurrentBaseProduct =
        typeof currentBaseProduct === 'string' ? currentBaseProduct.toLowerCase() : null;
    const currentProductByPrice =
        typeof currentPriceId === 'string' && currentPriceId
            ? catalog.find((product) =>
                  product?.prices?.some((price) => price.stripe_price_id === currentPriceId),
              )
            : null;
    const currentProduct =
        currentProductByPrice ||
        catalog.find(
            (product) =>
                typeof product?.name === 'string' &&
                product.name.toLowerCase() === normalizedCurrentBaseProduct,
        );

    const hasOpenTrialContext =
        currentPrice == null ||
        normalizedCurrentBaseProduct === 'trial' ||
        currentBaseProduct === '';
    if (!currentProduct && !hasOpenTrialContext) {
        return { product: null, price: null, isCurrentPlan: false, hasAudienceBlocker };
    }

    let recommended = null;

    // The inactive legacy current product is present in the full catalogue but
    // absent from the active recommendation candidates. Its order still sets
    // the floor, so legacy customers are pointed to the next suitable active
    // plan without advertising or recommending their legacy product.
    const currentIndex = currentProduct ? products.indexOf(currentProduct) : -1;
    const currentOrder = Number.isFinite(currentProduct?.order) ? currentProduct.order : null;

    if (currentProduct && currentIndex === -1 && currentOrder === null) {
        return { product: null, price: null, isCurrentPlan: false, hasAudienceBlocker };
    }

    products.forEach((product, index) => {
        const isBelowCurrentProduct =
            currentIndex >= 0
                ? index < currentIndex
                : currentOrder !== null &&
                  (!Number.isFinite(product?.order) || product.order < currentOrder);
        if (isBelowCurrentProduct) {
            return;
        }

        const cheapestFitting = fittingPriceFor(eligibilityByProduct[index]);
        if (!cheapestFitting) {
            return;
        }

        // Ties go to the earlier product; products are sorted by order, lowest tier first
        if (!recommended || cheapestFitting.cost < recommended.price.cost) {
            recommended = { product, price: cheapestFitting };
        }
    });

    if (!recommended) {
        return { product: null, price: null, isCurrentPlan: false, hasAudienceBlocker };
    }

    return {
        product: recommended.product,
        price: recommended.price,
        isCurrentPlan: recommended.price.stripe_price_id === currentPrice?.stripe_price_id,
        hasAudienceBlocker,
    };
};
